import { Building2, ChevronRight, LoaderCircle } from "lucide-react";
import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "../api/client";
import type { TenantInfo } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { AuthShell } from "../components/AuthShell";
import { FormError } from "../components/FormControls";
import { Button } from "../components/ui";

export function SelectSupplierPage({ tenant }: { tenant: TenantInfo }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [error, setError] = React.useState("");

  if (auth.status === "authenticated") return <Navigate to="/" replace />;
  if (auth.status !== "selecting" || !auth.selection) {
    return <Navigate to="/login" replace />;
  }

  async function choose(supplierUserId: number) {
    setSelectedId(supplierUserId);
    setError("");
    try {
      await auth.selectSupplier(supplierUserId);
      navigate("/", { replace: true });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Não foi possível entrar."));
    } finally {
      setSelectedId(null);
    }
  }

  return (
    <AuthShell
      tenant={tenant}
      title="Escolha uma empresa"
      description="Seu acesso está vinculado a mais de uma empresa. Selecione onde deseja entrar."
    >
      <div className="space-y-3">
        {error && <FormError message={error} />}
        {auth.selection.suppliers.map((supplier) => {
          const loading = selectedId === supplier.supplierUserId;
          return (
            <Button
              key={supplier.supplierUserId}
              type="button"
              variant="outline"
              disabled={selectedId !== null}
              onClick={() => choose(supplier.supplierUserId)}
              className="h-auto w-full justify-start rounded-xl p-4 text-left hover:border-primary hover:bg-accent disabled:cursor-wait"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                <Building2 size={20} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-slate-900">
                  {supplier.name}
                </span>
                <span className="text-xs text-slate-500">
                  Acessar documentos e pendências
                </span>
              </span>
              {loading ? (
                <LoaderCircle
                  className="animate-spin text-teal-700"
                  size={18}
                />
              ) : (
                <ChevronRight className="text-slate-400" size={18} />
              )}
            </Button>
          );
        })}
      </div>
    </AuthShell>
  );
}
